/**
 * Company membership agreement (DocuSign) — the FE side of core
 * helpers/agreement.js. Single place for the agreement labels, the CLIK
 * credential gate and the friendly copy of the agreement error codes; the chips
 * (MyMemberStatusChip.jsx), the agreement card, the credential forms and the
 * enrolment wizard all read from here.
 *
 * Sequential signing: the client signs first (wizard step 4), the application
 * goes to the CLIK review in the SAW portal, the CLIK Reviewer's approval
 * releases the envelope to the CLIK countersigner, and only once CLIK has signed
 * is the agreement "executed" — the point from which the company may enter its
 * CLIK credentials. A rejection keeps the envelope paused (the client's
 * signature stays; the same envelope is released after resubmit + approve).
 *
 * Declined / voided (incl. expired — envelopes expire after 180 days) is the
 * end of that envelope. The client cannot revive it: Everest/CLIK resets the
 * agreement in the SAW portal, which voids it and sends the company back to the
 * agreement step (verification_status null, member "Document submission"),
 * and the client signs a NEW agreement in wizard step 4. Until that new
 * envelope exists the old one stays the latest — so the payloads keep reading
 * "voided" (or "declined") while a re-sign is what the company waits for
 * (isResignRequired).
 *
 * `agreement_status` is derived by the backend, never stored:
 *   awaiting_client_signature | awaiting_review | awaiting_clik_signature |
 *   executed | declined | voided — or null when no agreement was sent yet.
 * It is a separate axis from the member status (memberStatusOf): a company can
 * be "Approved" while its agreement is still "Waiting CLIK signature".
 */

export const AGREEMENT_STATUS = Object.freeze({
  awaiting_client_signature: { label: 'Waiting client signature', tone: 'warning' },
  awaiting_review: { label: 'Waiting CLIK review', tone: 'warning' },
  awaiting_clik_signature: { label: 'Waiting CLIK signature', tone: 'warning' },
  executed: { label: 'Agreement executed', tone: 'success' },
  declined: { label: 'Agreement declined', tone: 'error' },
  voided: { label: 'Agreement voided', tone: 'error' },
})

/**
 * The agreement status of a company payload: the flat `agreement_status` (list
 * rows, details, signing-status) or the detail `agreement.status` block. null
 * when no agreement was sent yet — or the backend predates the field.
 */
export function agreementStatusOf(row) {
  return row?.agreement_status || row?.agreement?.status || null
}

/** `{ label, tone }` of an agreement status, or null for "no agreement". */
export function agreementPresentation(status) {
  if (!status) return null
  if (AGREEMENT_STATUS[status]) return AGREEMENT_STATUS[status]
  // A status this build does not know yet: show it rather than hide it.
  const words = String(status).replace(/_/g, ' ')
  return { label: words.charAt(0).toUpperCase() + words.slice(1), tone: 'gray' }
}

/** True once the client's signature is in (CLIK review, countersignature or done). */
export function isClientSigned(status) {
  return ['awaiting_review', 'awaiting_clik_signature', 'executed'].includes(status)
}

/** Agreement statuses of an envelope that can never be signed again. */
export const ENDED_AGREEMENT_STATUSES = Object.freeze(['declined', 'voided'])

/** True for a declined / voided (incl. expired) agreement. */
export function isAgreementEnded(status) {
  return ENDED_AGREEMENT_STATUSES.includes(status)
}

// The review verdict of a company payload: the flat verification_status (list
// rows, session seed, result / billing details) or the `verification` block
// (information detail). undefined when the payload carries neither.
function verificationStatusOf(company) {
  if (company?.verification_status !== undefined) return company.verification_status
  return company?.verification?.status
}

/**
 * Must the client sign the membership agreement again? True when the latest
 * agreement was declined / voided AND the company has no review verdict
 * (verification_status null — member "Document submission"): it is at the
 * agreement step — still enrolling, or Everest/CLIK reset the agreement — and
 * signs a new one by continuing the registration (step 4 sends a new envelope).
 * With a verdict (pending / approved / rejected / active) the client cannot act
 * until Everest/CLIK resets it. A payload without the verification fields
 * answers false: never invite a re-sign that is not open.
 */
export function isResignRequired(company) {
  return isAgreementEnded(agreementStatusOf(company)) && verificationStatusOf(company) === null
}

/**
 * The agreement status to SHOW for a company: what it waits for. A company that
 * must re-sign shows "Waiting client signature" instead of the old envelope's
 * declined / voided. Pair with agreementPresentation.
 */
export function agreementDisplayStatusOf(company) {
  return isResignRequired(company) ? 'awaiting_client_signature' : agreementStatusOf(company)
}

/**
 * The envelope status the enrolment screens should act on, from a
 * signing-status answer: null when that envelope is declined / voided — it is
 * not out for signature any more, even if the client had signed it ("completed"
 * reads "client signed"), so the client signs a new one in step 4.
 */
export function signingEnvelopeStatus(signing) {
  if (!signing) return null
  if (isAgreementEnded(signing.agreement_status)) return null
  return signing.status || null
}

// Why the CLIK credential form is locked, per agreement status.
const GATE_NOTES = {
  awaiting_client_signature:
    'The membership agreement has not been signed yet. Sign it first; after the CLIK review and CLIK countersignature you can enter your CLIK credentials here.',
  awaiting_review:
    'Your signed membership agreement is waiting for the CLIK review. Once CLIK approves your application and countersigns the agreement, you can enter your CLIK credentials here.',
  awaiting_clik_signature:
    'Your application is approved and the membership agreement is now with CLIK for countersignature. You can enter your CLIK credentials once CLIK has signed it.',
  declined:
    'The membership agreement was declined and can no longer be signed. Everest/CLIK will reset it so you can sign it again — you will get an email when it is ready to sign.',
  voided:
    'The membership agreement was voided or has expired and can no longer be signed. Everest/CLIK will reset it so you can sign it again — you will get an email when it is ready to sign.',
}

// The company is back at the agreement step (isResignRequired): the old
// agreement is gone and the client signs a new one.
export const RESIGN_NOTE =
  'This membership agreement can no longer be signed. Continue the registration from the Company page to sign the agreement again.'

const RESIGN_GATE_NOTE = `${RESIGN_NOTE} After the CLIK review and CLIK countersignature you can enter your CLIK credentials here.`

const GATE_FALLBACK =
  'The membership agreement is not fully signed yet. You can enter your CLIK credentials once CLIK has countersigned it.'

/**
 * May this company enter / validate its CLIK credentials yet? Mirrors the
 * backend guard (core Service.agreement.assertActivationAllowed): only a
 * sequential-flow agreement that is not executed blocks activation. Allowed:
 *   - no agreement status (none sent, or a backend without the field)
 *   - executed
 *   - an old single-signer envelope (signing_flow "single")
 * The backend stays the authority — this only keeps the form from inviting a
 * submit that is bound to be refused.
 *
 * @returns {{ blocked: boolean, status: string|null, note: string|null }}
 */
export function credentialGate(company) {
  const status = agreementStatusOf(company)
  const flow = company?.agreement?.signing_flow ?? company?.signing_flow ?? null
  if (!status || status === 'executed' || flow === 'single') {
    return { blocked: false, status, note: null }
  }
  if (isResignRequired(company)) return { blocked: true, status, note: RESIGN_GATE_NOTE }
  return { blocked: true, status, note: GATE_NOTES[status] || GATE_FALLBACK }
}

/**
 * What happens next with a declined / voided agreement — the client signs again
 * (isResignRequired), or waits for Everest/CLIK to reset it. null for any other
 * agreement.
 */
export function endedAgreementNote(company) {
  const status = agreementStatusOf(company)
  if (!isAgreementEnded(status)) return null
  return isResignRequired(company) ? RESIGN_NOTE : GATE_NOTES[status]
}

// Friendly copy for the agreement error codes the backend attaches to a
// FlowError body (`{ status, message, code, ...extras }`). The raw messages name
// internals (e.g. the DOCUSIGN_CLIK_SIGNER_EMAIL setting) or are terse.
const ERROR_MESSAGES = {
  // POST /company/:id/credentials/validate (409)
  awaiting_clik_signature:
    'CLIK has not countersigned the membership agreement yet. You can enter your CLIK credentials once CLIK has signed it.',
  // PATCH /company/:id/enroll/step-4 (503)
  docusign_clik_signer_not_configured:
    'The membership agreement cannot be sent right now because the CLIK signer has not been set up yet. Please try again later or contact us.',
  // step-4 (502)
  docusign_unavailable: 'DocuSign is unavailable right now. Please try again in a few minutes.',
  // POST /company/:id/enroll/refresh-signing-url (422)
  already_signed_by_client:
    'You have already signed the membership agreement. It is now with CLIK for review, so there is nothing left for you to sign.',
}

/** The friendly message for an agreement error code, or null when it is not one. */
export function agreementErrorMessage(err) {
  const code = err?.code
  if (code === 'agreement_not_executed') {
    // 409 for a declined / voided agreement; the body carries which one.
    return GATE_NOTES[err?.agreement_status] || GATE_NOTES.voided
  }
  return (code && ERROR_MESSAGES[code]) || null
}

/**
 * The rejected API body with its message swapped for the friendly one when it
 * carries an agreement error code — hand the result to myToaster /
 * myToasterFromApi. Anything else passes through untouched.
 */
export function withAgreementMessage(err) {
  const message = agreementErrorMessage(err)
  return message ? { ...err, message } : err
}

/** Agreement codes that mean "the agreement moved on — re-read the company". */
export const AGREEMENT_STALE_CODES = Object.freeze([
  'awaiting_clik_signature',
  'agreement_not_executed',
  'already_signed_by_client',
])
